import { methodConfig as defaultMethodConfig } from '@/config';
import { useConfig } from '@/lib/siteData';

interface MethodProps {
  index?: number;
  variant?: 'home' | 'about';
}

export function Method({ index, variant = 'home' }: MethodProps) {
  const config = useConfig('method_config', defaultMethodConfig);
  const steps = config.steps?.length ? config.steps : defaultMethodConfig.steps;

  if (steps.length === 0) return null;

  const sectionPadding =
    variant === 'about'
      ? 'py-16 md:py-24'
      : 'py-20 md:py-28';

  return (
    <section className={`w-full ${sectionPadding} bg-surface-elevated border-t border-surface-border-gray`}>
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          {/* Structure mirrors every other section (#16): a <span> numbered
              eyebrow (the short label) followed by the real section title as a
              proper <h2>. The number lives in the eyebrow span, never inside
              the heading, so heading hierarchy + SEO stay consistent. */}
          <span className="font-mono-label inline-block text-xs uppercase tracking-[0.2em] text-text-light font-normal">
            {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
            {config.label || 'THE CLEAR SYSTEMS METHOD'}
          </span>
          {config.heading && (
            <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.15] tracking-tight">
              {config.heading}
            </h2>
          )}
          {config.intro && (
            <p className="font-body text-base text-text-gray mt-4 leading-relaxed max-w-2xl">
              {config.intro}
            </p>
          )}
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((step, i) => (
            <li
              key={`${step.key}-${i}`}
              className="relative flex flex-col p-5 rounded-xl border border-surface-border-gray bg-surface-gray/50"
            >
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-geist-mono text-3xl font-medium text-accent-blue leading-none">
                  {step.key}
                </span>
                <span className="font-mono-label text-[10px] uppercase tracking-wider text-text-light">
                  Step {i + 1}
                </span>
              </div>
              <h3 className="font-heading text-base font-semibold text-text-primary leading-snug mb-2">
                {step.title}
              </h3>
              <p className="font-body text-sm text-text-gray leading-relaxed">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
